// Builds and downloads the student assessment report as a Word
// (.docx) document, using the `docx` library — the same one used
// in TeacherResources/YouTubeSummaryGenerator — and the same
// download technique: Packer.toBlob() + a temporary <a download>
// anchor click.
//
// This intentionally replaces an earlier iframe + html2pdf.js
// approach (rendering HTML into a hidden iframe via a `blob:` src,
// then rasterizing it). That approach broke in production because
// the site's Content-Security-Policy `frame-src` directive does
// not allow `blob:`, so the iframe never loaded and
// `iframe.contentDocument` came back null. An <a download> click on
// a blob: URL is a download action, not a frame navigation, so it
// is not subject to `frame-src` at all — which is why the
// YouTube summary's Word download already worked in production.
//
// This report is written for PARENTS — plain language, full skill
// text (no abbreviations), and large enough text to read
// comfortably. Only ACHIEVED skills are listed per DOK — it is a
// summary of what the student has accomplished, not the full
// skill checklist a teacher sees while assessing.

import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    AlignmentType,
    BorderStyle,
    WidthType,
    ShadingType,
    HeightRule,
    TabStopType,
    convertInchesToTwip,
} from "docx";

import { PROFICIENCY_COLORS } from "./batchAssessmentColors";

const hex = (color) => String(color || "").replace(/^#/, "");

const BRAND_GREEN = "738B3B";
const BRAND_GREEN_DARK = "546A2F";
const BRAND_GREEN_DARKER = "3E4F22";
const TEXT_COLOR = "2B2E24";
const MUTED_COLOR = "6B6F5E";
const BORDER_COLOR = "DCE0D2";
const SOFT_BG = "F7F8F3";
const WHITE = "FFFFFF";
const TRACK_BG = "E4E7DA";
const HEADER_EYEBROW = "D8E4C4";
const HEADER_SUBTEXT = "EDF1E3";

// A distinct identity color per DOK level — separate from the
// Emerging/Developing/Proficient/Extending proficiency palette so
// the two color systems never collide/read as the same signal.
const DOK_ACCENT_COLORS = [
    { text: "#2F7A78", bg: "#E9F3F2" },
    { text: "#5B4B8A", bg: "#F0EDF7" },
    { text: "#B8860B", bg: "#FBF3E0" },
    { text: "#A2325A", bg: "#F9ECF1" },
];

const PROFICIENCY_ORDER = [
    "Emerging",
    "Developing",
    "Proficient",
    "Extending",
];

// Plain-language explanations for parents unfamiliar with DOK
// terminology — static, not student-specific.
const DOK_LEVEL_DESCRIPTIONS = {
    1: "Foundational knowledge, facts, procedures, and routine application.",
    2: "Conceptual understanding, comparison, classification, and application across familiar contexts.",
    3: "Reasoning, justification, problem solving, and selecting an appropriate strategy.",
    4: "Transfer, synthesis, multi-step application, creation, and explanation in new or complex contexts.",
};

const PROFICIENCY_DESCRIPTIONS = {
    Emerging: "Beginning to demonstrate the skill.",
    Developing:
        "Demonstrates the skill with some support or inconsistency.",
    Proficient:
        "Consistently demonstrates the expected learning.",
    Extending:
        "Applies learning with depth, flexibility, or complexity.",
};

const formatDate = (isoDate) =>
    new Date(isoDate).toLocaleDateString(
        "en-US",
        {
            month: "long",
            day: "numeric",
            year: "numeric",
        }
    );

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: WHITE };

const NO_CELL_BORDERS = {
    top: NO_BORDER,
    bottom: NO_BORDER,
    left: NO_BORDER,
    right: NO_BORDER,
};

const NO_TABLE_BORDERS = {
    top: NO_BORDER,
    bottom: NO_BORDER,
    left: NO_BORDER,
    right: NO_BORDER,
    insideHorizontal: NO_BORDER,
    insideVertical: NO_BORDER,
};

const thinBorder = (color = BORDER_COLOR) => ({
    style: BorderStyle.SINGLE,
    size: 4,
    color,
});

const fullWidth = { size: 100, type: WidthType.PERCENTAGE };

// A single filler paragraph — every table cell needs at least one
// block child, including the thin "bar segment" cells used for the
// progress meters below.
const fillerParagraph = () =>
    new Paragraph({
        children: [new TextRun({ text: " ", size: 2 })],
    });

const spacerParagraph = (after = 200) =>
    new Paragraph({ text: "", spacing: { after } });

// Renders a proficiency value as a pill-style run: colored text on
// a colored run-level background (docx supports background
// shading per run, which is the closest equivalent to a CSS badge
// available in a Word document).
const proficiencyBadgeRun = (value, { size = 20 } = {}) => {
    const palette = PROFICIENCY_COLORS[value];

    if (!value || !palette) {
        return new TextRun({
            text: "Not yet set",
            size,
            color: MUTED_COLOR,
            italics: true,
        });
    }

    return new TextRun({
        text: `  ${value}  `,
        bold: true,
        size,
        color: hex(palette.text),
        shading: {
            type: ShadingType.CLEAR,
            fill: hex(palette.bg),
        },
    });
};

// ---------------------------------------------------------------
// Header
// ---------------------------------------------------------------

const buildHeaderTable = (reportData) => {
    const { student, subjectName, unitName, termNumber } = reportData;

    const metaParts = [`Class ${student.classLevel}`];

    if (student.sectionLevel) {
        metaParts.push(`Section ${student.sectionLevel}`);
    }

    if (student.academicYear) {
        metaParts.push(student.academicYear);
    }

    const unitLine = termNumber
        ? `${unitName}   •   Term ${termNumber}`
        : unitName;

    return new Table({
        width: fullWidth,
        borders: NO_TABLE_BORDERS,
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        shading: {
                            type: ShadingType.CLEAR,
                            fill: BRAND_GREEN_DARKER,
                        },
                        margins: {
                            top: 260,
                            bottom: 280,
                            left: 320,
                            right: 320,
                        },
                        borders: NO_CELL_BORDERS,
                        children: [
                            new Paragraph({
                                tabStops: [
                                    {
                                        type: TabStopType.RIGHT,
                                        position: convertInchesToTwip(6.5),
                                    },
                                ],
                                spacing: { after: 220 },
                                children: [
                                    new TextRun({
                                        text: "GRADELY",
                                        bold: true,
                                        size: 15,
                                        color: HEADER_EYEBROW,
                                    }),
                                    new TextRun({
                                        text: "\tSTUDENT PROGRESS REPORT",
                                        bold: true,
                                        size: 15,
                                        color: HEADER_EYEBROW,
                                    }),
                                ],
                            }),
                            new Paragraph({
                                spacing: { after: 60 },
                                children: [
                                    new TextRun({
                                        text: student.name,
                                        bold: true,
                                        size: 44,
                                        color: WHITE,
                                    }),
                                ],
                            }),
                            new Paragraph({
                                spacing: { after: 240 },
                                children: [
                                    new TextRun({
                                        text: metaParts.join("   •   "),
                                        size: 20,
                                        color: HEADER_SUBTEXT,
                                    }),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: subjectName,
                                        bold: true,
                                        size: 27,
                                        color: WHITE,
                                    }),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: unitLine,
                                        size: 19,
                                        color: HEADER_SUBTEXT,
                                    }),
                                ],
                            }),
                        ],
                    }),
                ],
            }),
        ],
    });
};

const buildReportDateParagraph = (generatedAt) =>
    new Paragraph({
        alignment: AlignmentType.RIGHT,
        spacing: { before: 160, after: 200 },
        children: [
            new TextRun({
                text: `Report Date: ${formatDate(generatedAt)}`,
                size: 16,
                color: MUTED_COLOR,
            }),
        ],
    });

// ---------------------------------------------------------------
// Overall Assessment + Skills Achieved summary row
// ---------------------------------------------------------------

const buildOverallMeterTable = (overallAssessment) => {
    const currentIndex = PROFICIENCY_ORDER.indexOf(overallAssessment);

    const barCells = PROFICIENCY_ORDER.map((label, index) => {
        const reached = currentIndex >= 0 && index <= currentIndex;
        const palette = PROFICIENCY_COLORS[label];

        return new TableCell({
            width: { size: 25, type: WidthType.PERCENTAGE },
            shading: {
                type: ShadingType.CLEAR,
                fill: reached ? hex(palette.text) : TRACK_BG,
            },
            margins: { top: 0, bottom: 0, left: 0, right: 0 },
            borders: NO_CELL_BORDERS,
            children: [fillerParagraph()],
        });
    });

    const labelCells = PROFICIENCY_ORDER.map((label, index) => {
        const isCurrent = index === currentIndex;
        const palette = PROFICIENCY_COLORS[label];

        return new TableCell({
            width: { size: 25, type: WidthType.PERCENTAGE },
            borders: NO_CELL_BORDERS,
            margins: { top: 60, bottom: 0, left: 0, right: 0 },
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                        new TextRun({
                            text: label,
                            bold: isCurrent,
                            size: 16,
                            color: isCurrent
                                ? hex(palette.text)
                                : MUTED_COLOR,
                        }),
                    ],
                }),
            ],
        });
    });

    return new Table({
        width: fullWidth,
        borders: NO_TABLE_BORDERS,
        rows: [
            new TableRow({
                height: { value: 160, rule: HeightRule.EXACT },
                children: barCells,
            }),
            new TableRow({ children: labelCells }),
        ],
    });
};

const buildSkillsProgressBar = (achieved, total) => {
    const percent =
        total > 0 ? Math.round((achieved / total) * 100) : 0;

    const clamped = Math.max(0, Math.min(100, percent));

    const cells = [];

    if (clamped > 0) {
        cells.push(
            new TableCell({
                width: { size: clamped, type: WidthType.PERCENTAGE },
                shading: {
                    type: ShadingType.CLEAR,
                    fill: BRAND_GREEN,
                },
                borders: NO_CELL_BORDERS,
                children: [fillerParagraph()],
            })
        );
    }

    if (clamped < 100) {
        cells.push(
            new TableCell({
                width: {
                    size: 100 - clamped,
                    type: WidthType.PERCENTAGE,
                },
                shading: {
                    type: ShadingType.CLEAR,
                    fill: TRACK_BG,
                },
                borders: NO_CELL_BORDERS,
                children: [fillerParagraph()],
            })
        );
    }

    return {
        percent,
        table: new Table({
            width: fullWidth,
            borders: NO_TABLE_BORDERS,
            rows: [
                new TableRow({
                    height: { value: 150, rule: HeightRule.EXACT },
                    children: cells,
                }),
            ],
        }),
    };
};

const buildSummarySection = (
    reportData,
    totalAchieved,
    totalSkills
) => {
    const { percent, table: progressBarTable } =
        buildSkillsProgressBar(totalAchieved, totalSkills);

    const overallCell = new TableCell({
        width: { size: 60, type: WidthType.PERCENTAGE },
        shading: { type: ShadingType.CLEAR, fill: SOFT_BG },
        margins: { top: 220, bottom: 220, left: 260, right: 300 },
        borders: {
            top: thinBorder(),
            bottom: thinBorder(),
            left: thinBorder(),
            right: NO_BORDER,
        },
        children: [
            new Paragraph({
                spacing: { after: 140 },
                children: [
                    new TextRun({
                        text: "Overall Assessment",
                        bold: true,
                        size: 22,
                        color: TEXT_COLOR,
                    }),
                ],
            }),
            new Paragraph({
                spacing: { after: 180 },
                children: [
                    proficiencyBadgeRun(
                        reportData.overallAssessment,
                        { size: 24 }
                    ),
                ],
            }),
            buildOverallMeterTable(reportData.overallAssessment),
        ],
    });

    const skillsCell = new TableCell({
        width: { size: 40, type: WidthType.PERCENTAGE },
        shading: { type: ShadingType.CLEAR, fill: WHITE },
        margins: { top: 220, bottom: 220, left: 300, right: 260 },
        borders: {
            top: thinBorder(),
            bottom: thinBorder(),
            right: thinBorder(),
            left: NO_BORDER,
        },
        children: [
            new Paragraph({
                spacing: { after: 140 },
                children: [
                    new TextRun({
                        text: "Skills Achieved",
                        bold: true,
                        size: 22,
                        color: TEXT_COLOR,
                    }),
                ],
            }),
            new Paragraph({
                spacing: { after: 160 },
                children: [
                    new TextRun({
                        text: String(totalAchieved),
                        bold: true,
                        size: 40,
                        color: BRAND_GREEN_DARK,
                    }),
                    new TextRun({
                        text: ` of ${totalSkills}`,
                        size: 22,
                        color: MUTED_COLOR,
                    }),
                ],
            }),
            progressBarTable,
            new Paragraph({
                spacing: { before: 120 },
                children: [
                    new TextRun({
                        text: `${percent}% of unit skills achieved`,
                        size: 16,
                        color: MUTED_COLOR,
                    }),
                ],
            }),
        ],
    });

    return new Table({
        width: fullWidth,
        borders: NO_TABLE_BORDERS,
        rows: [
            new TableRow({ children: [overallCell, skillsCell] }),
        ],
    });
};

// ---------------------------------------------------------------
// Educator comment
// ---------------------------------------------------------------

const buildEducatorCommentTable = (comment) => {
    if (!comment) {
        return null;
    }

    return new Table({
        width: fullWidth,
        borders: NO_TABLE_BORDERS,
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        shading: {
                            type: ShadingType.CLEAR,
                            fill: WHITE,
                        },
                        margins: {
                            top: 200,
                            bottom: 200,
                            left: 320,
                            right: 300,
                        },
                        borders: {
                            top: thinBorder(),
                            bottom: thinBorder(),
                            right: thinBorder(),
                            left: {
                                style: BorderStyle.SINGLE,
                                size: 24,
                                color: BRAND_GREEN,
                            },
                        },
                        children: [
                            new Paragraph({
                                spacing: { after: 100 },
                                children: [
                                    new TextRun({
                                        text: "TEACHER'S COMMENT",
                                        bold: true,
                                        size: 16,
                                        color: BRAND_GREEN_DARK,
                                    }),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: comment,
                                        italics: true,
                                        size: 22,
                                        color: TEXT_COLOR,
                                    }),
                                ],
                            }),
                        ],
                    }),
                ],
            }),
        ],
    });
};

// ---------------------------------------------------------------
// Learning Evidence — DOK sections
// ---------------------------------------------------------------

const buildLearningEvidenceHeading = () => [
    new Paragraph({
        spacing: { before: 200, after: 60 },
        children: [
            new TextRun({
                text: "Learning Evidence",
                bold: true,
                size: 30,
                color: TEXT_COLOR,
            }),
        ],
    }),
    new Paragraph({
        spacing: { after: 220 },
        children: [
            new TextRun({
                text:
                    "Skills are organized by depth of knowledge to show " +
                    "what the student can recall, apply, reason " +
                    "through, and extend.",
                size: 18,
                color: MUTED_COLOR,
            }),
        ],
    }),
];

const buildDokSectionTable = (dok, index) => {
    const accent = DOK_ACCENT_COLORS[index % DOK_ACCENT_COLORS.length];
    const accentText = hex(accent.text);
    const accentBg = hex(accent.bg);

    const headerCell = new TableCell({
        shading: { type: ShadingType.CLEAR, fill: accentBg },
        margins: { top: 180, bottom: 180, left: 260, right: 260 },
        borders: NO_CELL_BORDERS,
        children: [
            new Paragraph({
                tabStops: [
                    {
                        type: TabStopType.RIGHT,
                        position: convertInchesToTwip(6.3),
                    },
                ],
                spacing: { after: 60 },
                children: [
                    new TextRun({
                        text: `  DOK ${dok.level}  `,
                        bold: true,
                        size: 20,
                        color: WHITE,
                        shading: {
                            type: ShadingType.CLEAR,
                            fill: accentText,
                        },
                    }),
                    new TextRun({
                        text: `   ${dok.description}`,
                        bold: true,
                        size: 24,
                        color: TEXT_COLOR,
                    }),
                    new TextRun({ text: "\t" }),
                    proficiencyBadgeRun(dok.proficiency, { size: 18 }),
                ],
            }),
            new Paragraph({
                children: [
                    new TextRun({
                        text: DOK_LEVEL_DESCRIPTIONS[dok.level] || "",
                        size: 16,
                        color: MUTED_COLOR,
                    }),
                ],
            }),
        ],
    });

    const skillRows = dok.achievedSkills.map(
        (skill, skillIndex) =>
            new TableRow({
                children: [
                    new TableCell({
                        shading: {
                            type: ShadingType.CLEAR,
                            fill:
                                skillIndex % 2 === 0
                                    ? WHITE
                                    : SOFT_BG,
                        },
                        margins: {
                            top: 100,
                            bottom: 100,
                            left: 300,
                            right: 260,
                        },
                        borders: NO_CELL_BORDERS,
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "✓  ",
                                        bold: true,
                                        color: BRAND_GREEN,
                                        size: 20,
                                    }),
                                    new TextRun({
                                        text: skill.description,
                                        size: 22,
                                        color: TEXT_COLOR,
                                    }),
                                ],
                            }),
                        ],
                    }),
                ],
            })
    );

    return new Table({
        width: fullWidth,
        borders: {
            top: thinBorder(),
            bottom: thinBorder(),
            right: thinBorder(),
            left: {
                style: BorderStyle.SINGLE,
                size: 28,
                color: accentText,
            },
            insideHorizontal: thinBorder(),
            insideVertical: NO_BORDER,
        },
        rows: [
            new TableRow({ children: [headerCell] }),
            ...skillRows,
        ],
    });
};

const buildEmptyEvidenceParagraph = () =>
    new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 200, after: 200 },
        children: [
            new TextRun({
                text:
                    "No skills have been assessed yet for this unit.",
                italics: true,
                size: 22,
                color: MUTED_COLOR,
            }),
        ],
    });

// ---------------------------------------------------------------
// Proficiency scale legend
// ---------------------------------------------------------------

const buildProficiencyLegendSection = () => {
    const cells = PROFICIENCY_ORDER.map((label) => {
        const palette = PROFICIENCY_COLORS[label];
        const borderColor = hex(palette.border);

        return new TableCell({
            width: { size: 25, type: WidthType.PERCENTAGE },
            shading: {
                type: ShadingType.CLEAR,
                fill: hex(palette.bg),
            },
            margins: { top: 160, bottom: 160, left: 180, right: 180 },
            borders: {
                top: thinBorder(borderColor),
                bottom: thinBorder(borderColor),
                left: thinBorder(borderColor),
                right: thinBorder(borderColor),
            },
            children: [
                new Paragraph({
                    spacing: { after: 60 },
                    children: [
                        new TextRun({
                            text: label,
                            bold: true,
                            size: 18,
                            color: hex(palette.text),
                        }),
                    ],
                }),
                new Paragraph({
                    children: [
                        new TextRun({
                            text: PROFICIENCY_DESCRIPTIONS[label],
                            size: 14,
                            color: TEXT_COLOR,
                        }),
                    ],
                }),
            ],
        });
    });

    return [
        new Paragraph({
            spacing: { before: 260, after: 140 },
            children: [
                new TextRun({
                    text: "Proficiency Scale",
                    bold: true,
                    size: 22,
                    color: TEXT_COLOR,
                }),
            ],
        }),
        new Table({
            width: fullWidth,
            borders: NO_TABLE_BORDERS,
            rows: [new TableRow({ children: cells })],
        }),
    ];
};

const buildFooterParagraph = (generatedAt) =>
    new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 400 },
        border: {
            top: {
                style: BorderStyle.SINGLE,
                size: 6,
                color: BORDER_COLOR,
                space: 8,
            },
        },
        children: [
            new TextRun({
                text: `Generated on ${formatDate(generatedAt)} • Gradely`,
                italics: true,
                size: 16,
                color: MUTED_COLOR,
            }),
        ],
    });

// ---------------------------------------------------------------
// Document assembly
// ---------------------------------------------------------------

const generateStudentReportDocument = (reportData) => {
    const {
        student,
        overallAssessment: _overallAssessment,
        educatorComment,
        dokGroups,
        generatedAt,
    } = reportData;

    const reportedDokGroups = dokGroups.filter(
        (dok) => dok.achievedCount > 0
    );

    const totalAchieved = dokGroups.reduce(
        (sum, dok) => sum + dok.achievedCount,
        0
    );

    const totalSkills = dokGroups.reduce(
        (sum, dok) => sum + dok.totalCount,
        0
    );

    const children = [
        buildHeaderTable(reportData),
        buildReportDateParagraph(generatedAt),
        buildSummarySection(reportData, totalAchieved, totalSkills),
        spacerParagraph(),
    ];

    const commentTable = buildEducatorCommentTable(educatorComment);

    if (commentTable) {
        children.push(commentTable, spacerParagraph());
    }

    children.push(...buildLearningEvidenceHeading());

    if (reportedDokGroups.length > 0) {
        reportedDokGroups.forEach((dok, index) => {
            children.push(buildDokSectionTable(dok, index));
            children.push(spacerParagraph());
        });
    } else {
        children.push(buildEmptyEvidenceParagraph());
    }

    children.push(...buildProficiencyLegendSection());
    children.push(buildFooterParagraph(generatedAt));

    return new Document({
        creator: "Gradely",
        title: `Assessment Report - ${student.name}`,
        styles: {
            default: {
                document: {
                    run: {
                        font: "Calibri",
                        size: 22,
                        color: TEXT_COLOR,
                    },
                },
            },
        },
        sections: [
            {
                properties: {
                    page: {
                        size: {
                            width: convertInchesToTwip(8.5),
                            height: convertInchesToTwip(11),
                        },
                        margin: {
                            top: convertInchesToTwip(0.6),
                            bottom: convertInchesToTwip(0.6),
                            left: convertInchesToTwip(0.75),
                            right: convertInchesToTwip(0.75),
                        },
                    },
                },
                children,
            },
        ],
    });
};

// ---------------------------------------------------------------
// Download — same technique as
// TeacherResources/YouTubeSummaryGenerator.downloadWordDocument:
// Packer.toBlob() then a temporary <a download> anchor click. No
// iframe, no framed blob: URL, so it isn't affected by the
// frame-src CSP directive.
// ---------------------------------------------------------------

export const downloadStudentReportWord = async (reportData) => {
    if (!reportData) {
        return;
    }

    const doc = generateStudentReportDocument(reportData);
    const blob = await Packer.toBlob(doc);

    const safeStudentName = (
        reportData.student.name || "Student"
    ).replace(/[^a-z0-9]+/gi, "_");

    const safeUnitName = (
        reportData.unitName || "Unit"
    ).replace(/[^a-z0-9]+/gi, "_");

    const element = document.createElement("a");
    element.href = URL.createObjectURL(blob);
    element.download =
        `${safeStudentName}_Assessment_Report_${safeUnitName}.docx`;

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(element.href);
};
